/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useChartDataCursor } from '#hooks/PRIVATE_useChartDataCursor';
import { useChartDatatip } from '#hooks/PRIVATE_useChartDatatip';
import { LineAreaItem } from '#UNSAFE_LineAreaChart/lineAreaChart.types';
import { ScatterItem } from '#UNSAFE_ScatterChart/scatterChart.types';
import { BarItemData } from '#UNSAFE_BarChart/barChart.types';

type DataCursorProps = Parameters<typeof useChartDataCursor>[0];
type DatatipProps<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
> = Parameters<typeof useChartDatatip<K, D>>[0];

export function useChartDatatipAndCursor<
  K extends string | number,
  D extends LineAreaItem<K> | ScatterItem<K> | BarItemData<K>
>({
  series,
  leafGroupsInfo,
  width,
  height,
  findNearest,
  getMarkerInfo,
  onDataCursorPositionChange,
  isDataCursorEnabled,
  dataCursorPosition,
  dataCursorStyle,
  hoveredItemInfo,
  focusedItemInfo,
  getDataItem,
  getRefItem,
  getDataItemPos,
  getRefObjectBounds,
  getRefAxis,
  datatip,
  valueFormats,
  isHoriz,
  xScale,
  yScale,
  y2Scale,
  plotAreaSpace,
  timeAxisType,
  touchResponse,
  isDisabled,
  defaultXFormatter,
  defaultYFormatter,
  defaultY2Formatter,
  hasXYValues,
  getGroupFromPath,
  translations
}: DataCursorProps & DatatipProps<K, D>) {
  const { datatipContent, datatipProps } = useChartDatatip<K, D>({
    series,
    leafGroupsInfo,
    width,
    height,
    isDataCursorEnabled,
    dataCursorPosition,
    hoveredItemInfo,
    getDataItem,
    getRefItem,
    getDataItemPos,
    getRefObjectBounds,
    getRefAxis,
    focusedItemInfo,
    datatip,
    valueFormats,
    isHoriz,
    xScale,
    findNearest,
    timeAxisType,
    touchResponse,
    isDisabled,
    defaultXFormatter,
    defaultYFormatter,
    defaultY2Formatter,
    hasXYValues,
    translations,
    getGroupFromPath
  });

  const { dataCursorContent, dataCursorProps } = useChartDataCursor({
    touchResponse,
    findNearest,
    dataCursorPosition,
    onDataCursorPositionChange,
    isDataCursorEnabled,
    dataCursorStyle,
    isHoriz,
    getMarkerInfo,
    focusedItemInfo,
    plotAreaSpace,
    yScale,
    y2Scale,
    xScale,
    getDataItemPos
  });

  return {
    datatipContent,
    dataCursorContent,
    datatipProps: mergeProps(dataCursorProps, datatipProps)
  };
}
